'use client';

import { useState } from 'react';
import { useStorefrontData } from './storefront-data';

export type LeadKind = 'contact' | 'newsletter';

export interface LeadPayload {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  /** Honeypot: real visitors never fill a hidden "website" field. */
  website?: string;
}

/**
 * Shared submitter for the public contact and newsletter forms. The builder
 * canvas sets `preview` on the storefront context, so a test submission there
 * shows the success state without writing a real lead.
 */
export function useLeadSubmit(kind: LeadKind, source: string) {
  const { preview = false } = useStorefrontData();
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (payload: LeadPayload): Promise<boolean> => {
    if (preview) {
      setDone(true);
      setError(null);
      return true;
    }

    setPending(true);
    setError(null);

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ type: kind, source, ...payload }),
      });
      const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

      if (!response.ok) {
        setError(
          typeof data.error === 'string'
            ? data.error
            : typeof data.message === 'string'
              ? data.message
              : 'Could not send your message. Please try again.',
        );
        return false;
      }

      setDone(true);
      return true;
    } catch {
      setError('Could not send your message. Please try again.');
      return false;
    } finally {
      setPending(false);
    }
  };

  return {
    preview,
    pending,
    done,
    error,
    submit,
    reset: () => {
      setDone(false);
      setError(null);
    },
  };
}
